import { ApiError, requestJson, type ApiEnvelope } from '@shared/api/http';
import { assetSizeError, chatFileSizeError } from '@shared/media/uploadPolicy';

const normalizeBaseUrl = (baseUrl: string) => baseUrl.replace(/\/$/, '');
const isAbsoluteHttpUrl = (value: string) => value.startsWith('http://') || value.startsWith('https://');

export type MediaDomain = 'chat' | 'article' | 'avatar' | 'profile' | 'profileBackground' | 'system';
export type MediaKind = 'image' | 'video' | 'audio' | 'file';

export type MediaAsset = {
  id: string;
  mediaId?: string;
  domain: MediaDomain;
  kind: MediaKind;
  provider: string;
  storageProvider: string;
  objectKey: string;
  contentType: string;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  url: string;
};

type AssetUploadSession = {
  id: string;
  assetId: string;
  provider: string;
  bucket: string;
  objectKey: string;
  url?: string;
  contentType: string;
  sizeBytes: number;
  upload: {
    method: 'PUT';
    url: string;
    headers?: Record<string, string>;
    expiresAt: number;
  };
};

const mediaUrlOf = (asset?: Partial<MediaAsset> | null) => (
  typeof asset?.url === 'string' && asset.url
    ? asset.url
    : ''
);

export async function uploadMedia(
  baseUrl: string,
  file: File,
  options: {
    domain: MediaDomain;
    kind: MediaKind;
    visibility?: string;
    scopeType?: 'single' | 'group';
    scopeId?: number;
    metadata?: Record<string, unknown>;
    signal?: AbortSignal;
  },
): Promise<MediaAsset> {
  const privateChatFile = options.domain === 'chat' && options.kind === 'file' && options.visibility === 'chat_scope';
  const sizeError = privateChatFile ? chatFileSizeError(file.size) : assetSizeError(file.size);
  if (sizeError) throw new ApiError(sizeError, 413);
  if ((options.kind === 'image' || options.kind === 'video') &&
      options.metadata?.policyVersion !== 'media-client-v1') {
    throw new ApiError('媒体必须先完成客户端处理后才能上传', 422, {
      code: 'media_processing_required',
    });
  }
  const contentType = file.type || 'application/octet-stream';
  const sessionBody: Record<string, unknown> = {
    domain: options.domain,
    kind: options.kind,
    filename: file.name,
    contentType,
    sizeBytes: file.size,
    ...(options.visibility ? { visibility: options.visibility } : {}),
    ...(options.scopeType ? { scopeType: options.scopeType } : {}),
    ...(options.scopeId != null ? { scopeId: options.scopeId } : {}),
    ...(options.metadata ? { metadata: options.metadata } : {}),
  };

  const sessionEnvelope = await requestJson<ApiEnvelope<AssetUploadSession>>('/assets/upload-session', {
    baseUrl,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(sessionBody),
    signal: options.signal,
  });
  if (sessionEnvelope.status && sessionEnvelope.status !== 'Success') {
    throw new ApiError(sessionEnvelope.message || '创建上传会话失败', 400, sessionEnvelope);
  }
  const session = sessionEnvelope.data;
  if (!session?.assetId || !session.upload?.url) {
    throw new ApiError('创建上传会话失败: 缺少上传 URL', 500, sessionEnvelope);
  }
  if (!isAbsoluteHttpUrl(session.upload.url)) {
    throw new ApiError('创建上传会话失败: 上传 URL 必须是绝对地址', 500, sessionEnvelope);
  }

  const putHeaders = new Headers(session.upload.headers ?? {});
  if (!putHeaders.has('Content-Type')) {
    putHeaders.set('Content-Type', contentType);
  }
  const uploadResponse = await fetch(session.upload.url, {
    method: session.upload.method,
    headers: putHeaders,
    body: file,
    signal: options.signal ? AbortSignal.any([options.signal, AbortSignal.timeout(120000)]) : AbortSignal.timeout(120000),
  });
  if (!uploadResponse.ok) {
    throw new ApiError(`上传对象失败: ${uploadResponse.status}`, uploadResponse.status, await uploadResponse.text());
  }

  const completeEnvelope = await requestJson<ApiEnvelope<MediaAsset>>(`/assets/${encodeURIComponent(session.assetId)}/complete`, {
    baseUrl,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    signal: options.signal,
  });
  if (completeEnvelope.status && completeEnvelope.status !== 'Success') {
    throw new ApiError(completeEnvelope.message || '完成上传失败', 400, completeEnvelope);
  }

  const completed = completeEnvelope.data;
  const stableUrl = mediaUrlOf(completed);
  if (!privateChatFile && !isAbsoluteHttpUrl(stableUrl)) {
    throw new ApiError('完成上传失败: 资源 URL 必须是绝对地址', 500, completeEnvelope);
  }

  return {
    id: session.assetId,
    mediaId: session.assetId,
    domain: options.domain,
    kind: options.kind,
    provider: 's3',
    storageProvider: 's3',
    objectKey: session.objectKey,
    contentType,
    sizeBytes: file.size,
    width: typeof options.metadata?.width === 'number' ? options.metadata.width : null,
    height: typeof options.metadata?.height === 'number' ? options.metadata.height : null,
    durationMs: typeof options.metadata?.durationMs === 'number' ? options.metadata.durationMs : null,
    url: stableUrl,
  };
}

export async function downloadChatFile(
  assetId: string,
  fileName: string,
  baseUrl: string,
): Promise<void> {
  const envelope = await requestJson<ApiEnvelope<{ url?: string }>>(
    `/assets/${encodeURIComponent(assetId)}/url`, {
      baseUrl,
      headers: { 'X-Locs-Page-Origin': window.location.origin },
    },
  );
  const signedUrl = envelope.data?.url;
  if (!signedUrl || !isAbsoluteHttpUrl(signedUrl)) {
    throw new ApiError('获取文件下载权限失败', 502, envelope);
  }
  if (window.location.protocol === 'https:' && new URL(signedUrl).protocol !== 'https:') {
    throw new ApiError('下载链接不安全，文件未能下载，请稍后重试', 502, envelope);
  }
  const anchor = document.createElement('a');
  anchor.href = signedUrl;
  const basename = fileName.replaceAll('\\', '/').split('/').pop()?.trim() || '';
  const safeName = basename.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_');
  anchor.download = safeName && safeName !== '.' && safeName !== '..' ? safeName : 'download.bin';
  anchor.rel = 'noopener';
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
  }
}

export async function fetchAssetMetadata(
  id: string,
  baseUrl: string,
): Promise<MediaAsset> {
  const envelope = await requestJson<ApiEnvelope<MediaAsset>>(`/assets/${encodeURIComponent(id)}`, {
    baseUrl,
  });
  if (envelope.status && envelope.status !== 'Success') {
    throw new ApiError(envelope.message || '获取资源失败', 400, envelope);
  }
  if (!envelope.data?.id) {
    throw new ApiError('获取资源失败: 缺少数据', 500, envelope);
  }
  return envelope.data;
}

/**
 * Fetch a single MediaAsset by its MongoDB ObjectId.
 * Kept for metadata views; media playback/rendering should use the stable
 * URL already present in the asset payload.
 */
export async function fetchMediaAsset(
  id: string,
  baseUrl: string,
): Promise<MediaAsset> {
  const body = await requestJson<ApiEnvelope<MediaAsset>>(`/media/${encodeURIComponent(id)}`, {baseUrl});
  if (!body?.data?.id) {
    throw new ApiError('获取媒体资产失败: 缺少数据', 502, body);
  }
  return body.data;
}
